import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import {
  UniversalFilterDateValue,
  UniversalFilterDateModes,
  UniversalFilterDateFilterState,
  UniversalFilterDateOnFilterStateChange,
  UniversalFilterDateOnChange
} from '../../UniversalFilterDate.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterDateFilterState } from '../useUniversalFilterDateFilterState';

import { UniversalFilterDateIsAfterModeOptions } from '../../components/UniversalFilterDateIsAfterMode';
import { UniversalFilterDateIsBeforeModeOptions } from '../../components/UniversalFilterDateIsBeforeMode';
import { UniversalFilterDateIsModeOptions } from '../../components/UniversalFilterDateIsMode';
import { UniversalFilterDateIsOnOrAfterModeOptions } from '../../components/UniversalFilterDateIsOnOrAfterMode';
import { UniversalFilterDateIsOnOrBeforeModeOptions } from '../../components/UniversalFilterDateIsOnOrBeforeMode';
import { UniversalFilterDateIsWithinModeOptions } from '../../components/UniversalFilterDateIsWithinMode';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterDateModes.IS;

const defaultOptionsByMode = {
  [UniversalFilterDateModes.IS]: UniversalFilterDateIsModeOptions.EXACT_DAY,
  [UniversalFilterDateModes.IS_WITHIN]:
    UniversalFilterDateIsWithinModeOptions.THE_NEXT_NUMBER_OF_DAYS,
  [UniversalFilterDateModes.IS_BEFORE]:
    UniversalFilterDateIsBeforeModeOptions.EXACT_DAY,
  [UniversalFilterDateModes.IS_AFTER]:
    UniversalFilterDateIsAfterModeOptions.EXACT_DAY,
  [UniversalFilterDateModes.IS_ON_OR_BEFORE]:
    UniversalFilterDateIsOnOrBeforeModeOptions.EXACT_DAY,
  [UniversalFilterDateModes.IS_ON_OR_AFTER]:
    UniversalFilterDateIsOnOrAfterModeOptions.EXACT_DAY
};

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterDateModes>[] =
  [
    { value: UniversalFilterDateModes.IS, i18nText: filtersKeys.modes.is },
    {
      value: UniversalFilterDateModes.IS_WITHIN,
      i18nText: filtersKeys.modes.isWithin
    },
    {
      value: UniversalFilterDateModes.IS_BEFORE,
      i18nText: filtersKeys.modes.isBefore
    },
    {
      value: UniversalFilterDateModes.IS_AFTER,
      i18nText: filtersKeys.modes.isAfter
    },
    {
      value: UniversalFilterDateModes.IS_ON_OR_BEFORE,
      i18nText: filtersKeys.modes.isOnOrBefore
    },
    {
      value: UniversalFilterDateModes.IS_ON_OR_AFTER,
      i18nText: filtersKeys.modes.isOnOrAfter
    },
    // {
    //   value: UniversalFilterDateModes.IS_NOT,
    //   i18nText: filtersKeys.modes.isNot
    // },
    {
      value: UniversalFilterDateModes.IS_EMPTY,
      i18nText: filtersKeys.modes.isEmpty
    },
    {
      value: UniversalFilterDateModes.IS_NOT_EMPTY,
      i18nText: filtersKeys.modes.isNotEmpty
    }
  ];

interface UniversalFilterDateOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterDateValue;
  onChange: UniversalFilterDateOnChange;
}

function useUniversalFilterDate({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterDateOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterDateFilterState>(filterStateCacheKey);

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterDateFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterDateFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          option: defaultOptionsByMode[defaultMode]
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterDateOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterDateModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        option: defaultOptionsByMode[mode]
      });
    },
    [handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleFilterStateChange
  };
}

export default useUniversalFilterDate;
